import type {
  TokenRequest, TokenResponse, QueueNextRequest, QueueAvailabilityResponse,
  ChargeRequest, ChargeResponse,
  BalanceResponse, ConsumeRequest,
  ErrorResponse,
  LedgerEntryRow, LedgerEntryKind,
  QueueNextResponse,
  SetupIntentResponse, PaymentMethodResponse, SavePaymentMethodRequest,
  PurchaseIntentRequest, PurchaseIntentResponse,
  ChargeSavedRequest, ChargeSavedResponse,
  ConfirmPurchaseRequest, ConfirmPurchaseResponse,
} from '@aslgo/shared';
import { supabase } from '../lib/supabase.js';
import { getValidAccessToken, forceRefresh, readSession } from '../lib/session.js';

const BASE = '/api';
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '');
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function readError(res: Response): Promise<never> {
  let msg = `HTTP ${res.status}`;
  try {
    const body = await res.json() as ErrorResponse;
    if (body.error) msg = body.error;
  } catch { /* ignore non-JSON error body */ }
  throw new ApiError(res.status, msg);
}

function buildHeaders(init: RequestInit, token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    ...(init.headers ?? {}),
    Authorization: `Bearer ${token}`,
  };
}

async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  // getValidAccessToken refreshes via refresh_token if the stored access_token
  // is expired (or within 30s of expiry). This is the only refresh path —
  // supabase-js's autoRefreshToken is disabled (see lib/supabase.ts).
  let token = await getValidAccessToken();
  if (!token) {
    // No usable session. Fire signOut so onAuthStateChange resets the store
    // and routes the user to LoginView (signOut on an already-empty session
    // is a no-op).
    await supabase.auth.signOut().catch(() => { /* best-effort */ });
    throw new ApiError(401, 'not signed in');
  }
  let res = await fetch(`${BASE}${path}`, { ...init, headers: buildHeaders(init, token) });
  if (res.status === 401) {
    // Backend rejected an apparently-valid token (clock skew, rotation race
    // with another tab, or the session was invalidated server-side). Try one
    // forced refresh + retry before giving up.
    const refreshed = await forceRefresh();
    if (refreshed) {
      res = await fetch(`${BASE}${path}`, { ...init, headers: buildHeaders(init, refreshed) });
    }
    if (res.status === 401) {
      await supabase.auth.signOut().catch(() => { /* best-effort */ });
    }
  }
  return res;
}

export async function requestToken(req: TokenRequest): Promise<TokenResponse> {
  const res = await authedFetch('/token', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.ok) await readError(res);
  return res.json() as Promise<TokenResponse>;
}

export async function chargeCard(req: ChargeRequest): Promise<ChargeResponse> {
  const res = await authedFetch('/billing/charge', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.ok) await readError(res);
  return res.json() as Promise<ChargeResponse>;
}

export async function consumeMinutes(req: ConsumeRequest): Promise<BalanceResponse> {
  const res = await authedFetch('/billing/consume', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.ok) await readError(res);
  return res.json() as Promise<BalanceResponse>;
}

// --------------------------------------------------------------------------
// Card-on-file (Setup Element) + purchase (Payment Element) helpers. The card
// number itself never touches this code — it lives only in Stripe's iframe;
// we only ever pass around a setupIntentId/paymentIntentId and display the
// brand/last4/exp the backend returns.
// --------------------------------------------------------------------------

export async function createSetupIntent(): Promise<SetupIntentResponse> {
  const res = await authedFetch('/billing/setup-intent', { method: 'POST', body: JSON.stringify({}) });
  if (!res.ok) await readError(res);
  return res.json() as Promise<SetupIntentResponse>;
}

export async function savePaymentMethod(setupIntentId: string): Promise<PaymentMethodResponse> {
  const res = await authedFetch('/billing/payment-method', {
    method: 'POST',
    body: JSON.stringify({ setupIntentId } satisfies SavePaymentMethodRequest),
  });
  if (!res.ok) await readError(res);
  return res.json() as Promise<PaymentMethodResponse>;
}

export async function getPaymentMethod(): Promise<PaymentMethodResponse> {
  const res = await authedFetch('/billing/payment-method', { method: 'GET' });
  if (!res.ok) await readError(res);
  return res.json() as Promise<PaymentMethodResponse>;
}

export async function deletePaymentMethod(): Promise<PaymentMethodResponse> {
  const res = await authedFetch('/billing/payment-method', { method: 'DELETE' });
  if (!res.ok) await readError(res);
  return res.json() as Promise<PaymentMethodResponse>;
}

export async function createPurchaseIntent(packageId: string): Promise<PurchaseIntentResponse> {
  const res = await authedFetch('/billing/purchase-intent', {
    method: 'POST',
    body: JSON.stringify({ packageId } satisfies PurchaseIntentRequest),
  });
  if (!res.ok) await readError(res);
  return res.json() as Promise<PurchaseIntentResponse>;
}

export async function chargeSavedCard(packageId: string): Promise<ChargeSavedResponse> {
  const res = await authedFetch('/billing/charge-saved', {
    method: 'POST',
    body: JSON.stringify({ packageId } satisfies ChargeSavedRequest),
  });
  if (!res.ok) await readError(res);
  return res.json() as Promise<ChargeSavedResponse>;
}

export async function confirmPurchase(paymentIntentId: string, saveCard?: boolean): Promise<ConfirmPurchaseResponse> {
  const res = await authedFetch('/billing/confirm', {
    method: 'POST',
    body: JSON.stringify({
      paymentIntentId,
      ...(saveCard === undefined ? {} : { saveCard }),
    } satisfies ConfirmPurchaseRequest),
  });
  if (!res.ok) await readError(res);
  return res.json() as Promise<ConfirmPurchaseResponse>;
}

// --------------------------------------------------------------------------
// Supabase RPC helpers (talk to /rest/v1/rpc/... directly, not the backend).
// supabase-js's auth client wedges after tab-visibility changes (see the
// comment block in useAppStore.ts) so we read the token directly from
// localStorage and call the RPC over plain fetch — same pattern as
// loadProfileAndRoster() in the store. Reuses the module-level
// SUPABASE_URL + PUBLISHABLE_KEY declared above for the storage-key
// derivation; no need for a parallel set of constants.
// --------------------------------------------------------------------------

async function callRpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const token = await getValidAccessToken();
  if (!token) throw new ApiError(401, 'not signed in');
  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: PUBLISHABLE_KEY,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    let msg = `RPC ${name} failed (HTTP ${res.status})`;
    try {
      const body = await res.json() as { message?: string };
      if (body.message) msg = body.message;
    } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, msg);
  }
  // PostgREST returns 204 No Content with an empty body for functions that
  // return void (e.g. submit_call_rating). res.json() would throw on the
  // empty payload; short-circuit instead.
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function submitCallRating(sessionId: number, stars: number): Promise<void> {
  await callRpc<void>('submit_call_rating', { p_session_id: sessionId, p_stars: stars });
}

export function getSessionForRating(room: string): Promise<number | null> {
  return callRpc<number | null>('get_session_for_rating', { p_room: room });
}

/** Entries per window. The RPC caps at this too; a full page means the window
 *  was truncated, which the drawer reports rather than showing a partial month
 *  as if it were complete. */
export const LEDGER_WINDOW_LIMIT = 100;

interface RawLedgerRow {
  entry_key: string;
  kind: string;
  occurred_at: string;
  session_id: number | null;
  peer_name: string | null;
  minutes_delta: number;
  session_minutes: number | null;
  dollars: string | number | null;
  note: string | null;
  balance_after: number | null;
}

/** The caller's own ledger in [sinceISO, untilISO), already grouped by the
 *  database. Takes no user id — get_my_ledger reads auth.uid() itself. */
export async function getMyLedger(
  sinceISO: string, untilISO: string,
): Promise<LedgerEntryRow[]> {
  const rows = await callRpc<RawLedgerRow[] | null>('get_my_ledger', {
    p_since: sinceISO, p_until: untilISO, p_limit: LEDGER_WINDOW_LIMIT,
  });
  return (rows ?? []).map((r) => ({
    entryKey: r.entry_key,
    kind: r.kind as LedgerEntryKind,
    occurredAt: r.occurred_at,
    sessionId: r.session_id,
    peerName: r.peer_name,
    minutesDelta: r.minutes_delta,
    sessionMinutes: r.session_minutes,
    // Defensive: this project's PostgREST returns numeric as an unquoted JSON
    // number, but Number() is correct under either serialisation.
    dollars: r.dollars == null ? null : Number(r.dollars),
    note: r.note,
    balanceAfter: r.balance_after,
  }));
}

// --------------------------------------------------------------------------
// Profile page helpers.
// --------------------------------------------------------------------------

/**
 * Updates the caller's display name. Checks availability first (the same RPC
 * the signup flow uses), then PATCHes profiles directly — the authenticated
 * role has a column-scoped UPDATE grant on (display_name, avatar_url), so this
 * self-update goes straight through PostgREST under RLS. Throws ApiError on a
 * taken name (409), a not-allowed name (400, banned-words check), or any
 * other REST failure.
 */
export async function updateDisplayName(name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new ApiError(400, 'Display name is required');
  const status = await callRpc<string>('check_display_name', { name: trimmed });
  if (status === 'taken') throw new ApiError(409, 'That display name is already taken — choose a different one.');
  if (status === 'not_allowed') throw new ApiError(400, "That display name isn't allowed — choose a different one.");
  const token = await getValidAccessToken();
  if (!token) throw new ApiError(401, 'not signed in');
  const uid = readSession()?.user?.id;
  if (!uid) throw new ApiError(401, 'not signed in');
  const res = await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${uid}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      apikey: PUBLISHABLE_KEY,
      Authorization: `Bearer ${token}`,
      Prefer: 'return=minimal',
    },
    body: JSON.stringify({ display_name: trimmed }),
  });
  if (!res.ok) await readError(res);
}

/**
 * Verifies the caller's current password by attempting a password grant.
 * Returns true on success. Does NOT persist the returned session — this is a
 * read-only credential check that gates the password change; the live session
 * (its refresh token) is left untouched.
 */
export async function verifyCurrentPassword(email: string, password: string): Promise<boolean> {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { apikey: PUBLISHABLE_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return res.ok;
}

/**
 * Updates the signed-in user's password via GoTrue directly (PUT /auth/v1/user
 * with the current access token). We use direct fetch rather than
 * supabase.auth.updateUser because supabase-js's auth client is documented to
 * wedge on this app's long-lived, externally-managed sessions (see the comment
 * block in useAppStore.ts). Callers should verify the current password first.
 */
export async function updatePassword(newPassword: string): Promise<void> {
  const token = await getValidAccessToken();
  if (!token) throw new ApiError(401, 'not signed in');
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      apikey: PUBLISHABLE_KEY,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ password: newPassword }),
  });
  if (!res.ok) {
    let msg = `password update failed (HTTP ${res.status})`;
    try {
      const b = (await res.json()) as { msg?: string; error_description?: string; message?: string };
      msg = b.error_description ?? b.msg ?? b.message ?? msg;
    } catch { /* non-JSON */ }
    throw new ApiError(res.status, msg);
  }
}

export interface GuideProfile {
  phone: string | null;
  availability: string | null;
  availabilitySlots: string[];
  smsOptIn: boolean;
}

/** Reads the caller's own guide_profiles row (RLS self-select). Null if none. */
export async function getMyGuideProfile(): Promise<GuideProfile | null> {
  const token = await getValidAccessToken();
  if (!token) throw new ApiError(401, 'not signed in');
  const uid = readSession()?.user?.id;
  if (!uid) throw new ApiError(401, 'not signed in');
  const url = new URL(`${SUPABASE_URL}/rest/v1/guide_profiles`);
  url.searchParams.set('select', 'phone,availability,availability_slots,sms_opt_in');
  url.searchParams.set('profile_id', `eq.${uid}`);
  const res = await fetch(url.toString(), {
    headers: { apikey: PUBLISHABLE_KEY, Authorization: `Bearer ${token}` },
  });
  if (!res.ok) await readError(res);
  const rows = (await res.json()) as Array<{
    phone: string | null; availability: string | null;
    availability_slots: string[] | null; sms_opt_in: boolean;
  }>;
  const row = rows[0];
  if (!row) return null;
  return {
    phone: row.phone,
    availability: row.availability,
    availabilitySlots: row.availability_slots ?? [],
    smsOptIn: row.sms_opt_in,
  };
}

export interface GuideProfileUpdate {
  phone?: string;
  availability?: string;
  availabilitySlots?: string[];
  smsOptIn?: boolean;
}

/** Writes guide profile fields (phone / availability / SMS opt-in) via the
 *  backend service-role route. Returns the persisted values. */
export async function updateGuideProfile(body: GuideProfileUpdate): Promise<GuideProfile> {
  const res = await authedFetch('/profile/guide', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) await readError(res);
  const data = (await res.json()) as {
    phone: string | null; availability: string | null;
    availabilitySlots: string[]; smsOptIn: boolean;
  };
  return {
    phone: data.phone,
    availability: data.availability,
    availabilitySlots: data.availabilitySlots ?? [],
    smsOptIn: data.smsOptIn,
  };
}

/** Sends a feedback message to the team via the backend (Postmark). */
export async function sendFeedback(message: string): Promise<void> {
  const trimmed = message.trim();
  if (!trimmed) throw new ApiError(400, 'Please enter a message');
  const res = await authedFetch('/feedback', {
    method: 'POST',
    body: JSON.stringify({ message: trimmed }),
  });
  if (!res.ok) await readError(res);
}

/**
 * Sends a post-session safety report via the backend (Postmark). The session id
 * and peer name are attached automatically so the report can be tied to a call
 * without asking a distressed reporter for identifiers.
 */
export async function sendSafetyReport(
  message: string,
  sessionId: number | null,
  peer: string | null,
): Promise<void> {
  const trimmed = message.trim();
  if (!trimmed) throw new ApiError(400, 'Please describe what happened');
  const res = await authedFetch('/safety', {
    method: 'POST',
    body: JSON.stringify({ message: trimmed, sessionId, peer }),
  });
  if (!res.ok) await readError(res);
}

/** Ask the queue for the next Ready guide. `durationMinutes` and `topic` ride
 *  along so the backend can put them in the VoIP push when the guide it picks
 *  is Ready but not in the lobby room (the learner's own lobby invite carries
 *  them for a guide who is). */
export async function queueNext(
  exclude: string[] = [],
  opts: { durationMinutes?: number; topic?: string } = {},
): Promise<QueueNextResponse> {
  const body: QueueNextRequest = { exclude };
  if (opts.durationMinutes !== undefined) body.durationMinutes = opts.durationMinutes;
  if (opts.topic !== undefined) body.topic = opts.topic;
  const res = await authedFetch('/queue/next', { method: 'POST', body: JSON.stringify(body) });
  if (!res.ok) await readError(res);
  return res.json() as Promise<QueueNextResponse>;
}

/** How many guides the queue could reach right now (lobby-Ready ∪ push-reachable,
 *  excluding the caller). Read-only; the lobby polls it while a learner is idle
 *  so a Ready iOS guide whose app is closed still enables Start. */
export async function getQueueAvailability(): Promise<QueueAvailabilityResponse> {
  const res = await authedFetch('/queue/availability', { method: 'GET' });
  if (!res.ok) await readError(res);
  return res.json() as Promise<QueueAvailabilityResponse>;
}

export async function queueRelease(): Promise<void> {
  await authedFetch('/queue/release', { method: 'POST', body: JSON.stringify({}) }).catch(() => { /* best-effort */ });
}

export async function queueReady(): Promise<void> {
  await authedFetch('/queue/ready', { method: 'POST', body: JSON.stringify({}) }).catch(() => { /* best-effort */ });
}

export async function queueStandby(): Promise<void> {
  await authedFetch('/queue/standby', { method: 'POST', body: JSON.stringify({}) }).catch(() => { /* best-effort */ });
}
